import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ICart, ICartItem, IPicture } from "../../models";
import { AppState } from "../main.reducer";
import {
  ICartReducerState,
  IPictureFrame,
  IPictureFrames,
  IPictureQuantity,
} from "./cart.reducer";
import { orderApi } from "./api/orderApi";
import { resetCartThunk } from "./thunk/resetCartThunk";
import {
  CHANGE_BRANDED_BAG_NUMBER,
  CHANGE_COLOR_PACK_NUMBER,
  CHANGE_EASEL_ALUM_NUMBER,
  CHANGE_EASEL_WOOD_NUMBER,
  CHANGE_FRAME_NUMBER,
  HIDE_TOAST_CART_NOTIFICATION,
  LOAD_CART,
  LOAD_EASEL_ALUM,
  LOAD_EASEL_WOOD,
  LOAD_FRAMES,
  LOAD_RESERVE,
  SET_CART_PAGE,
  SET_PICTURE_FRAMES,
} from "./cart.actions";
import {
  ADDITIONAL_EQUIPMENT_CATEGORY,
  CODE_BRANDED_BAG,
  CODE_COLOR_PACK,
  CODE_EASEL_ALUM,
  CODE_EASEL_WOOD,
  CODE_FRAME,
  CODE_FRAME_ASSEMBLED,
  Currency,
  ID_BRANDED_BAG,
  ID_COLOR_PACK,
  ID_EASEL_ALUM,
  ID_EASEL_WOOD,
  ID_FRAME_ASSEMBLED,
  KIDS_CATEGORY,
  SPEC_CATEGORIES,
  WEIGHT_COLOR_PACK,
  WEIGHT_EASEL_ALUMINIUM,
  WEIGHT_EASEL_WOOD,
  WEIGHT_FRAME,
  WEIGHT_PICTURE,
} from "../../common/constants";
import { getEquipmentPrice } from "../../common/utils";
import { isBih, localizePictures } from "../Pictures/catalog";

const NO_FRAME: IPictureFrame = { frames: 0, assembled: 0 };

/** Kids motifs already come on a blind frame; equipment has no frame at all. */
export const canHaveFrame = (pic: IPicture) =>
  !SPEC_CATEGORIES.includes(pic.category);

export const useCart = () => {
  const {
    page,
    cart,
    easelWood,
    easelAlum,
    frames,
    pictureFrames,
    colorPack,
    brandedBag,
    reserve,
    notifyCart,
    notifyCartModal,
  } = useSelector<AppState, ICartReducerState>((store) => store.cart);

  const allPictures = useSelector<AppState, IPicture[]>(
    (store) => store.pictures.allPictures
  );

  const currency = useSelector<AppState, Currency>(
    (store) => store.common.currency
  );

  // Pictures as the visitor's country sees them (hidden ones left out, status = local stock)
  const catalogPictures = localizePictures(allPictures, currency);

  const equipmentPrice = useMemo(() => {
    const prices = getEquipmentPrice(allPictures);
    return {
      ...prices,
      shipping: currency === "KM" ? prices.shippingBih : prices.shipping,
    };
  }, [allPictures, currency]);

  const dispatch = useDispatch();

  const setPage = (page: number) => dispatch({ type: SET_CART_PAGE, page });
  const orderSubmit = (newCart: ICart, result: (id: number) => void) =>
    orderApi(dispatch, newCart, result);
  const resetCart = () => resetCartThunk(dispatch);

  const loadCart = (cart: { [pictureId: number]: number }) =>
    dispatch({ type: LOAD_CART, cart });
  const loadReserve = (reserve: { [pictureId: number]: number }) =>
    dispatch({ type: LOAD_RESERVE, reserve });
  const loadEaselWood = (number: number) =>
    dispatch({ type: LOAD_EASEL_WOOD, number });
  const loadEaselAlum = (number: number) =>
    dispatch({ type: LOAD_EASEL_ALUM, number });
  const loadFrames = (frames: number) =>
    dispatch({ type: LOAD_FRAMES, frames });
  const loadPictureFrames = (pictureFrames: IPictureFrames) =>
    dispatch({ type: SET_PICTURE_FRAMES, pictureFrames });
  const loadColorPack = (quantity: number) =>
    dispatch({ type: CHANGE_COLOR_PACK_NUMBER, quantity });
  const loadBrandedBag = (quantity: number) =>
    dispatch({ type: CHANGE_BRANDED_BAG_NUMBER, quantity });

  const cartPictures = useCallback(
    () =>
      catalogPictures.filter((pic) => cart && cart[pic.id] && cart[pic.id] > 0),
    [catalogPictures, cart]
  );
  const cartReservations = useCallback(
    () =>
      catalogPictures.filter(
        (pic) => reserve && reserve[pic.id] && reserve[pic.id] > 0
      ),
    [catalogPictures, reserve]
  );

  /** Assembled frame can be ordered for this picture in the visitor's country. */
  const canHaveFrameAssembled = useCallback(
    (pic: IPicture) =>
      canHaveFrame(pic) && pic.frameAssembled && !isBih(currency),
    [currency]
  );

  // The stored choice clamped to what is allowed now: never more than the quantity,
  // no frames for kids/equipment, no assembled frames where they can't be ordered (BiH).
  const effectivePictureFrames = useMemo(() => {
    const result: IPictureFrames = {};
    cartPictures().forEach((pic) => {
      const stored = pictureFrames[pic.id] || NO_FRAME;
      const quantity = cart[pic.id];
      if (!canHaveFrame(pic)) {
        return;
      }
      const assembled = canHaveFrameAssembled(pic)
        ? Math.min(stored.assembled, quantity)
        : 0;
      const plain = Math.min(stored.frames, quantity - assembled);
      if (assembled > 0 || plain > 0) {
        result[pic.id] = { frames: plain, assembled };
      }
    });
    return result;
  }, [cartPictures, pictureFrames, cart, canHaveFrameAssembled]);

  const getPictureFrame = (pictureId: number): IPictureFrame =>
    effectivePictureFrames[pictureId] || NO_FRAME;

  /** Assembled frames: each one belongs to a picture. */
  const framesAssembled = useMemo(
    () =>
      Object.values(effectivePictureFrames).reduce(
        (sum, f) => sum + f.assembled,
        0
      ),
    [effectivePictureFrames]
  );

  /** Plain frames: chosen for pictures plus the extra ones without a picture. */
  const framesTotal = useMemo(
    () =>
      frames +
      Object.values(effectivePictureFrames).reduce(
        (sum, f) => sum + f.frames,
        0
      ),
    [frames, effectivePictureFrames]
  );

  const savePictureFrames = (newPictureFrames: IPictureFrames) => {
    localStorage.setItem("bpb.pictureFrames", JSON.stringify(newPictureFrames));
    dispatch({ type: SET_PICTURE_FRAMES, pictureFrames: newPictureFrames });
  };

  /** Sets the frame choice of one picture, capped by its quantity. */
  const setPictureFrame = (
    pictureId: number,
    plain: number,
    assembled: number,
    quantity: number = cart[pictureId] || 0
  ) => {
    const newPictureFrames = { ...pictureFrames };
    const a = Math.max(0, Math.min(assembled, quantity));
    const f = Math.max(0, Math.min(plain, quantity - a));
    if (a > 0 || f > 0) {
      newPictureFrames[pictureId] = { frames: f, assembled: a };
    } else {
      delete newPictureFrames[pictureId];
    }
    savePictureFrames(newPictureFrames);
  };

  /** BiH warning: drop every assembled frame. */
  const removeAllAssembled = () => {
    const newPictureFrames: IPictureFrames = {};
    Object.entries(pictureFrames).forEach(([id, f]) => {
      if (f.frames > 0) {
        newPictureFrames[Number(id)] = { frames: f.frames, assembled: 0 };
      }
    });
    savePictureFrames(newPictureFrames);
  };

  /** BiH warning: send the assembled frames unassembled instead. */
  const convertAssembledToFrames = () => {
    const newPictureFrames: IPictureFrames = {};
    Object.entries(pictureFrames).forEach(([id, f]) => {
      newPictureFrames[Number(id)] = {
        frames: f.frames + f.assembled,
        assembled: 0,
      };
    });
    savePictureFrames(newPictureFrames);
  };

  /**
   * Carts from before frames were chosen per picture only kept counts. Hand them out to the
   * pictures in the cart (assembled first, where allowed); plain frames that don't fit stay
   * as extra frames. Runs once, the old keys are removed afterwards.
   */
  const migrateLegacyFrames = () => {
    const legacyAssembled = localStorage.getItem("bpb.framesAssembled");
    if (legacyAssembled === null || catalogPictures.length === 0) {
      return;
    }
    let assembledLeft = Number(JSON.parse(legacyAssembled)) || 0;
    let plainLeft = frames;
    const newPictureFrames: IPictureFrames = {};
    const framed = cartPictures().filter(canHaveFrame);

    framed.filter(canHaveFrameAssembled).forEach((pic) => {
      const a = Math.min(assembledLeft, cart[pic.id]);
      if (a > 0) {
        newPictureFrames[pic.id] = { frames: 0, assembled: a };
        assembledLeft -= a;
      }
    });
    plainLeft += assembledLeft;
    framed.forEach((pic) => {
      const used = newPictureFrames[pic.id]?.assembled || 0;
      const f = Math.min(plainLeft, cart[pic.id] - used);
      if (f > 0) {
        newPictureFrames[pic.id] = { frames: f, assembled: used };
        plainLeft -= f;
      }
    });

    savePictureFrames(newPictureFrames);
    localStorage.setItem("bpb.frames", JSON.stringify(plainLeft));
    dispatch({ type: LOAD_FRAMES, frames: plainLeft });
    localStorage.removeItem("bpb.framesAssembled");
    localStorage.removeItem("bpb.framesAsPictures");
    localStorage.removeItem("bpb.framesAssembledAsPictures");
  };

  /**
   * A picture in the cart that is out of stock in the visitor's country (e.g. after switching
   * DIN -> KM) can only be reserved: move it to the reservations, without its frames.
   */
  const moveOutOfStockToReserve = () => {
    const outOfStock = cartPictures().filter((pic) => !pic.status);
    if (outOfStock.length === 0) {
      return;
    }
    const newCart: IPictureQuantity = { ...cart };
    const newReserve: IPictureQuantity = { ...reserve };
    const newPictureFrames: IPictureFrames = { ...pictureFrames };
    outOfStock.forEach((pic) => {
      newReserve[pic.id] = (newReserve[pic.id] || 0) + cart[pic.id];
      newCart[pic.id] = 0;
      delete newPictureFrames[pic.id];
    });
    localStorage.setItem("bpb.cart", JSON.stringify(newCart));
    localStorage.setItem("bpb.reserve", JSON.stringify(newReserve));
    dispatch({ type: LOAD_CART, cart: newCart });
    dispatch({ type: LOAD_RESERVE, reserve: newReserve });
    savePictureFrames(newPictureFrames);
  };

  const cartCount = useMemo(() => {
    if (cart) {
      return (
        Object.values(cart).reduce((partialSum, a) => partialSum + a, 0) +
        framesTotal +
        framesAssembled +
        easelWood +
        easelAlum
      );
    }
    return 0;
  }, [cart, framesTotal, framesAssembled, easelWood, easelAlum]);

  const reserveCount = () => {
    let q = 0;
    if (reserve) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [key, value] of Object.entries(reserve)) {
        q += value;
      }
    }
    return q;
  };

  const isCartEmpty = () => {
    return (
      cartPictures().length === 0 &&
      frames === 0 &&
      easelWood === 0 &&
      easelAlum === 0 &&
      colorPack === 0 &&
      brandedBag === 0 &&
      reserveCount() === 0
    );
  };

  // DEPRICIATED
  // const pricePictures = () => {
  //   let total = 0;
  //   cartPictures().forEach((p) => (total += p.price * cart[p.id]));
  //   return total;
  // };

  const pricePictures = () => {
    let total = 0;
    cartPictures()
      .filter((p) => !SPEC_CATEGORIES.includes(p.category)) // ONLY REGULAR PICTURES (CATEGORIES)
      .forEach((p) => (total += p.price * cart[p.id]));
    return total;
  };

  const minMaxPicturePrice = () => {
    const picPrices = cartPictures()
      .filter((p) => !SPEC_CATEGORIES.includes(p.category))
      .map((p) => p.price);

    const min = picPrices.length
      ? Math.min(...picPrices)
      : equipmentPrice.typicalPriceRegular;
    const max = picPrices.length
      ? Math.max(...picPrices)
      : equipmentPrice.typicalPriceRegular;

    return { min, max };
  };

  const pricePicturesKids = () => {
    let total = 0;
    cartPictures()
      .filter((p) => p.category === KIDS_CATEGORY)
      .forEach((p) => (total += p.price * cart[p.id]));
    return total;
  };

  const minMaxPicturePriceKids = () => {
    const picPrices = cartPictures()
      .filter((p) => p.category === KIDS_CATEGORY)
      .map((p) => p.price);

    const min = picPrices.length
      ? Math.min(...picPrices)
      : equipmentPrice.typicalPriceKids;
    const max = picPrices.length
      ? Math.max(...picPrices)
      : equipmentPrice.typicalPriceKids;

    return { min, max };
  };

  const priceEaselsWood = () => {
    let total = 0;
    total += easelWood * equipmentPrice.easelW;
    return total;
  };
  const priceEaselsAlum = () => {
    let total = 0;
    total += easelAlum * equipmentPrice.easelA;
    return total;
  };
  const priceFrames = () => {
    let total = 0;
    total += framesTotal * equipmentPrice.frame;
    return total;
  };
  const priceFramesAssembled = () => {
    let total = 0;
    total += framesAssembled * equipmentPrice.frameAssembled;
    return total;
  };
  const priceColorPack = () => {
    let total = 0;
    total += colorPack * equipmentPrice.colorPack;
    return total;
  };
  const priceBrandedBag = () => {
    let total = 0;
    total += brandedBag * equipmentPrice.brandedBag;
    return total;
  };
  const priceTotal = () => {
    let total = 0;
    cartPictures().forEach((p) => (total += p.price * cart[p.id]));
    total +=
      framesTotal * equipmentPrice.frame +
      framesAssembled * equipmentPrice.frameAssembled +
      easelWood * equipmentPrice.easelW +
      easelAlum * equipmentPrice.easelA +
      colorPack * equipmentPrice.colorPack +
      brandedBag * equipmentPrice.brandedBag +
      equipmentPrice.shipping;
    return total;
  };

  const countPrice = useMemo(() => {
    let total = 0;
    cartPictures()
      .filter((p) => !SPEC_CATEGORIES.includes(p.category))
      .map((p) => (total += cart[p.id]));
    return total;
  }, [cartPictures, cart]);

  const countPriceKids = useMemo(() => {
    let total = 0;
    cartPictures()
      .filter((p) => p.category === KIDS_CATEGORY)
      .map((p) => (total += cart[p.id]));
    return total;
  }, [cartPictures, cart]);

  const cartWeight =
    cartCount * WEIGHT_PICTURE +
    framesTotal * WEIGHT_FRAME +
    easelWood * WEIGHT_EASEL_WOOD +
    easelAlum * WEIGHT_EASEL_ALUMINIUM +
    colorPack * WEIGHT_COLOR_PACK;

  const cartItems: ICartItem[] = useMemo(() => {
    const cartItemPictures = cartPictures().map(
      (p) =>
        ({
          picture_id: p.id,
          code: p.code,
          price: p.price,
          quantity: cart[p.id],
          isReservation: false,
          category: p.category,
          codeSP: p.codeSP,
          frames: effectivePictureFrames[p.id]?.frames || undefined,
          framesAssembled: effectivePictureFrames[p.id]?.assembled || undefined,
        } as ICartItem)
    );
    const cartItemReservations = cartReservations().map(
      (r) =>
        ({
          picture_id: r.id,
          code: r.code,
          price: r.price,
          quantity: reserve[r.id],
          isReservation: true,
          category: r.category,
          codeSP: r.codeSP,
        } as ICartItem)
    );
    let cartAditionalItems = [] as ICartItem[];
    if (framesTotal > 0) {
      cartAditionalItems.push({
        picture_id: 0,
        code: CODE_FRAME,
        price: equipmentPrice.frame,
        quantity: framesTotal,
        isReservation: false,
        category: ADDITIONAL_EQUIPMENT_CATEGORY,
        codeSP: CODE_FRAME,
      } as ICartItem);
    }
    if (framesAssembled > 0) {
      cartAditionalItems.push({
        picture_id: ID_FRAME_ASSEMBLED,
        code: CODE_FRAME_ASSEMBLED,
        price: equipmentPrice.frameAssembled,
        quantity: framesAssembled,
        isReservation: false,
        category: ADDITIONAL_EQUIPMENT_CATEGORY,
        codeSP: undefined,
      } as ICartItem);
    }
    if (easelWood > 0) {
      cartAditionalItems.push({
        picture_id: ID_EASEL_WOOD,
        code: CODE_EASEL_WOOD,
        price: equipmentPrice.easelW,
        quantity: easelWood,
        isReservation: false,
        category: ADDITIONAL_EQUIPMENT_CATEGORY,
        codeSP: CODE_EASEL_WOOD,
      } as ICartItem);
    }
    if (easelAlum > 0) {
      cartAditionalItems.push({
        picture_id: ID_EASEL_ALUM,
        code: CODE_EASEL_ALUM,
        price: equipmentPrice.easelA,
        quantity: easelAlum,
        isReservation: false,
        category: ADDITIONAL_EQUIPMENT_CATEGORY,
        codeSP: CODE_EASEL_ALUM,
      } as ICartItem);
    }
    if (colorPack > 0) {
      cartAditionalItems.push({
        picture_id: ID_COLOR_PACK,
        code: CODE_COLOR_PACK,
        price: equipmentPrice.colorPack,
        quantity: colorPack,
        isReservation: false,
        category: ADDITIONAL_EQUIPMENT_CATEGORY,
        codeSP: CODE_COLOR_PACK,
      } as ICartItem);
    }
    if (brandedBag > 0) {
      cartAditionalItems.push({
        picture_id: ID_BRANDED_BAG,
        code: CODE_BRANDED_BAG,
        price: equipmentPrice.brandedBag,
        quantity: brandedBag,
        isReservation: false,
        category: ADDITIONAL_EQUIPMENT_CATEGORY,
        codeSP: CODE_BRANDED_BAG,
      } as ICartItem);
    }
    return [
      ...cartItemPictures,
      ...cartAditionalItems,
      ...cartItemReservations,
    ];
  }, [
    cart,
    reserve,
    cartPictures,
    cartReservations,
    effectivePictureFrames,
    easelAlum,
    easelWood,
    framesTotal,
    framesAssembled,
    colorPack,
    brandedBag,
    equipmentPrice,
  ]);

  const changeEaselWoodNumber = (quantity: number) => {
    localStorage.setItem("bpb.easelWood", JSON.stringify(quantity));
    dispatch({ type: CHANGE_EASEL_WOOD_NUMBER, quantity });
  };

  const changeEaselAlumNumber = (quantity: number) => {
    localStorage.setItem("bpb.easelAlum", JSON.stringify(quantity));
    dispatch({ type: CHANGE_EASEL_ALUM_NUMBER, quantity });
  };

  const changeFrameNumber = (quantity: number) => {
    if (frames !== quantity) {
      localStorage.setItem("bpb.frames", JSON.stringify(quantity));
      dispatch({ type: CHANGE_FRAME_NUMBER, quantity });
    }
  };

  const changeColorPackNumber = (quantity: number) => {
    if (colorPack !== quantity) {
      localStorage.setItem("bpb.colorPack", JSON.stringify(quantity));
      dispatch({ type: CHANGE_COLOR_PACK_NUMBER, quantity });
    }
  };

  const changeBrandedBagNumber = (quantity: number) => {
    if (brandedBag !== quantity) {
      localStorage.setItem("bpb.brandedBag", JSON.stringify(quantity));
      dispatch({ type: CHANGE_BRANDED_BAG_NUMBER, quantity });
    }
  };

  const hideNotificationCart = () => {
    dispatch({ type: HIDE_TOAST_CART_NOTIFICATION });
  };

  return {
    loadCart,
    loadReserve,
    loadEaselWood,
    loadEaselAlum,
    loadFrames,
    loadPictureFrames,
    loadColorPack,
    loadBrandedBag,
    page,
    setPage,
    cart,
    easelWood,
    easelAlum,
    frames,
    framesTotal,
    framesAssembled,
    getPictureFrame,
    setPictureFrame,
    canHaveFrameAssembled,
    removeAllAssembled,
    convertAssembledToFrames,
    migrateLegacyFrames,
    moveOutOfStockToReserve,
    colorPack,
    brandedBag,
    reserve,
    notifyCart,
    notifyCartModal,
    cartPictures: cartPictures(),
    cartReservations: cartReservations(),
    cartCount,
    reserveCount: reserveCount(),
    isCartEmpty: isCartEmpty(),
    // pricePictures,
    pricePictures,
    minMaxPicturePrice,
    pricePicturesKids,
    minMaxPicturePriceKids,
    priceFrames,
    priceFramesAssembled,
    priceEaselsWood,
    priceEaselsAlum,
    priceColorPack,
    priceBrandedBag,
    priceTotal,
    countPrice,
    countPriceKids,
    cartWeight,
    cartItems,
    orderSubmit,
    resetCart,
    changeEaselWoodNumber,
    changeEaselAlumNumber,
    changeFrameNumber,
    changeColorPackNumber,
    changeBrandedBagNumber,
    hideNotificationCart,
    equipmentPrice,
  };
};
